import { Panel, PanelType, Text, IconButton, Icon } from '@fluentui/react';
import { useFileBrowserStore } from '@/hooks';
import { ActivityType } from '@/types';

const getActivityIcon = (type: ActivityType): string => {
  switch (type) {
    case 'upload':
      return 'Upload';
    case 'delete':
      return 'Delete';
    case 'create_folder':
      return 'NewFolder';
    case 'download':
      return 'Download';
    case 'move':
      return 'MoveToFolder';
    case 'duplicate_skipped':
      return 'BlockedSite';
    case 'error':
      return 'ErrorBadge';
    default:
      return 'Info';
  }
};

const getActivityColor = (type: ActivityType): string => {
  switch (type) {
    case 'upload':
      return '#107c10'; // Green
    case 'delete':
      return '#a4262c'; // Red
    case 'create_folder':
      return '#0078d4'; // Blue
    case 'download':
      return '#8764b8'; // Purple
    case 'move':
      return '#986f0b'; // Orange/brown
    case 'duplicate_skipped':
      return '#797775'; // Gray
    case 'error':
      return '#a4262c'; // Red
    default:
      return '#323130';
  }
};

const formatTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) {
    return 'Just now';
  } else if (diff < 3600000) {
    const mins = Math.floor(diff / 60000);
    return `${mins}m ago`;
  } else if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours}h ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

interface ActivityLogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ActivityLog({ isOpen, onClose }: ActivityLogProps) {
  const { activityLog, clearActivityLog } = useFileBrowserStore();

  const handleClearLog = () => {
    clearActivityLog();
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onClose}
      type={PanelType.smallFixedFar}
      // headerText="Activity Log"
      isLightDismiss
      onRenderNavigation={() => (
        <div className="activity-panel-header">
          <Text variant="large" className="activity-panel-title">
            Activity Log
          </Text>
          <div className="activity-panel-actions">
            {activityLog.length > 0 && (
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Clear Log"
                onClick={handleClearLog}
                styles={{ root: { height: 32, width: 32 } }}
              />
            )}
            <IconButton
              iconProps={{ iconName: 'Cancel' }}
              title="Close"
              onClick={onClose}
              styles={{ root: { height: 32, width: 32 } }}
            />
          </div>
        </div>
      )}
    >
      <div className="activity-log">
        {activityLog.length === 0 ? (
          <div className="activity-empty">
            <Icon iconName="History" styles={{ root: { fontSize: 32, color: '#a19f9d' } }} />
            <Text className="activity-empty-text">No recent activity</Text>
          </div>
        ) : (
          <div className="activity-list">
            {activityLog.map((entry) => (
              <div key={entry.id} className={`activity-item activity-${entry.type}`}>
                <div className="activity-icon" style={{ color: getActivityColor(entry.type) }}>
                  <Icon iconName={getActivityIcon(entry.type)} />
                </div>
                <div className="activity-content">
                  <Text className="activity-message">{entry.message}</Text>
                  {entry.details && <Text className="activity-details">{entry.details}</Text>}
                  {entry.path && <Text className="activity-path">{entry.path}</Text>}
                </div>
                <div className="activity-time">
                  <Text>{formatTime(entry.timestamp)}</Text>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
